import { useCallback, useEffect, useRef, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { Popup } from "@/Common/Components/Modals/Popup";
import { PdfViewer } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { CustomFaxClient } from "@/ExportableFile/Api/CustomFaxClient";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FileTypeAttachment } from "@/ExportableFile/Models/FileTypes/FileTypeAttachment";
import { FileTypeRadPdf } from "@/ExportableFile/Models/FileTypes/FileTypeRadPdf";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { SignPrintAuthorizationOverlayElement } from "@/ExportableFile/Models/OverlayElements/SignPrintAuthorizationOverlayElement";
import { RadPdfExportableFileHelper } from "@/Patient/Helpers/RadPdfExportableFileHelper";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { SignedAuthorizationDocumentType } from "@/Patient/Models/MedicalNote/Enum/SignedAuthorizationDocumentType";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { PatientPrescriptionItem } from "@/Patient/Models/Medication/PatientPrescriptionItem";
import { PdfFormInboxItem } from "@/Patient/Models/ShareItems/PdfFormInboxItem";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { PlanActionLinks } from "../../PlanActionLinks";
import { RadPdfFormSettingPopup } from "./RadPdfFormSettingPopup";

import styles from "./RadPdfFormSection.module.scss";

interface IMessageEventData {
    eventTriggerKey: "sign" | "fax" | "print";
    radDocumentId: string;
    lastModificationDateUtc: Date | null;
}

interface RadPdfFormSectionProps {
    noteModel: IPatientMedicalNoteModel;
    prescriptions: Immutable.List<PatientPrescriptionItem>;
    isReadOnly: boolean;
    isEditMode: boolean;
    patientModel: Patient;
    patientId: Guid;
    currentEncounter: Encounter;
    getRevokeSignaturePopupModel: (id: string, type: SignedAuthorizationDocumentType) => void;
}

function RadPdfFormSection(props: RadPdfFormSectionProps): JSX.Element {
    const { getText } = useTranslation();

    const defaultZoomLevel: number = 6;
    const [radPdfFormStates, setRadPdfFormStates] = useState<
    {
        showRevokeDeleteMessageBeforeDeleting: boolean,
        formToDeleteId: string
    }>({ showRevokeDeleteMessageBeforeDeleting: false, formToDeleteId: null });
    const callbackList = useRef([]);
    const [pdfFormsData, setPdfFormsData] = useState<PdfFormInboxItem[]>([]);
    const [pdfZoomLevel, setPdfZoomLevel] = useState<number[]>([]);
    const [formIdsToDisableButtonToSign, setFormIdsToDisableButtonToSign] = useState<string[]>([]);
    const [isFormDataStale, setIsFormDataStale] = useState<boolean>(false);

    useEffect(() => {
        if (callbackList.current.length > 0) {
            callbackList.current.forEach(callback => callback());
            callbackList.current = [];
        }
    }, [radPdfFormStates]);

    const patientForms = props.noteModel?.get("PatientForms");
    const sentFaxes = props.noteModel?.get("SentFaxes") ?? {};

    useEffect(
        (): void => {
            setIsFormDataStale(false);
        },
        [patientForms]
    );

    useEffect(() => {
        const patientFormsInArray: PdfFormInboxItem[] = patientForms?.toArray() ?? [];

        setPdfFormsData(patientFormsInArray);
        setPdfZoomLevel(Array(patientFormsInArray.length).fill(defaultZoomLevel));
    }, [patientForms]);

    const onDeletePdfForm = (form: PdfFormInboxItem): void => {
        if (form.SharedItem) {
            callbackList.current.push(() => sessionStore.dispatch(
                LocalPatientOperations.MedicalNote.MedicalNoteForm_SetFormToRevoke,
                {
                    portalShareableItem: form
                }
            ));

            setRadPdfFormStates(prevState => ({ ...prevState, showRevokeDeleteMessageBeforeDeleting: true, formToDeleteId: form.Id }));
            return;
        }

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.DeletePdfForm, { PatientFormId: form.Id });
    };

    const onPdfFormClick = (docId: string, editMode: boolean): void => {
        const op = new LocalEvent(LocalPatientOperations.MedicalNote.OpenRadPdfForm);
        op.AddItem("patientFormId", docId);
        op.AddItem("editMode", editMode);
        LocalEventDispatcher.dispatch(op);
    };

    const showContextMenuForPdf = (form: PdfFormInboxItem): void => {
        const operation = new LocalEvent(LocalPatientOperations.MedicalNote.OpenContextMenuPdf);
        operation.AddItem("form", form);
        LocalEventDispatcher.dispatch(operation);
    };

    const enableCssForReduceFrictionSignature = cacheStore.ClinicSettings.EnableQuickDocumentSigning && !props.isReadOnly;

    const protectFormAndReloadData = useCallback(
        (currentEncounterId: Guid | string, patientId: Guid): void => {
            setIsFormDataStale(true);
            patientStore.ActionsMedicalNote.reloadEncounterForms(currentEncounterId, patientId);
        },
        []
    );

    const protectFormAndRevokeSignature = useCallback(
        (formId: string): void => {
            setIsFormDataStale(true);
            props.getRevokeSignaturePopupModel(
                formId,
                SignedAuthorizationDocumentType.PatientForm
            );
        },
        []
    );

    const faxOrPrintRadPdfFile = useCallback(
        async (form: PdfFormInboxItem, mode: ExportMode, lastModificationDateUtc: Date | null = null): Promise<void> => {
            const exportableFile = RadPdfExportableFileHelper.fromPdfFormInboxItem(form);

            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                if (mode === ExportMode.Sign) {
                    setFormIdsToDisableButtonToSign(p => p.concat(form.FormId));
                    const isFormSigned = await (async () => {
                        const currentUser: Readonly<Employee> = sessionStore.getCurrentEmployee().Model;
                        if (!currentUser?.SignatureByteArray) {
                            return false;
                        }

                        const token = LoginPinFrequencyModel.fromResponseModel(currentUser.LoginPinFrequency).formToken ?? "";
                        const isTokenValid = await ExportableFileHelper.validatePinFrequencyToken(token);
                        if (!isTokenValid) {
                            return false;
                        }

                        const isSameFormSignedBefore = (await CustomFaxClient.loadEmployeeFormSign(currentUser.Contact_id, form.FormId, ExportMode.Sign))?.ShouldSign;
                        if (!isSameFormSignedBefore) {
                            return false;
                        }

                        const response = await exportableFile?.clone().getFetchUrlDtoAsync(true, true, true);
                        const exportableFileWithSignature = response?.OriginalExportableFile as FileTypeRadPdf;
                        if (!exportableFileWithSignature?.overlayElements?.any()) {
                            return false;
                        }

                        const isCurrentFormAlreadySignedByCurrentEmployee = SignedPrintAuthorization.isLastSignatureFrom(
                            form.SignedPrintAuthorizations,
                            currentUser.ContactFacet_id,
                            form.EncounterId
                        );

                        if (!isCurrentFormAlreadySignedByCurrentEmployee) {
                            await exportableFileWithSignature.persistSignature(form.EncounterId, lastModificationDateUtc);
                            protectFormAndReloadData(form.EncounterId, props.patientId);
                            displayToast(MessageToast.Success, getText("DocumentSigningPinOptions_SignedSuccessfullyLabel"));
                        }

                        return true;
                    })();
                    setFormIdsToDisableButtonToSign(p => p.filter(formId => formId !== form.FormId));

                    if (isFormSigned) {
                        return;
                    }
                }
            }

            // if current user has no signature, print form directly, not in popup.
            if (
                !cacheStore.ClinicSettings.EnableQuickDocumentSigning
                && mode === ExportMode.Print
                && !sessionStore.getCurrentEmployee().Model.SignatureByteArray
            ) {
                return exportableFile.print();
            }

            const printAuthorization = SignedPrintAuthorization.getLast(form.SignedPrintAuthorizations);
            const isSignedByCurrentUser = printAuthorization?.CreatedByEmployeeId === sessionStore.getCurrentEmployee().ContactFacetId;
            const isSignedAuthorizationEnabled = cacheStore.ClinicSettings.EnableSignedAuthorization;
            const canUserSign =
                isSignedAuthorizationEnabled
                && (sessionStore.signOnPrintAccess || cacheStore.ClinicSettings.EnableQuickDocumentSigning)
                && !isSignedByCurrentUser;

            // load latest preSigned copy if available
            let preSignedCopy: FileTypeAttachment;
            if (mode == ExportMode.UseSigned || isSignedByCurrentUser) {
                if (printAuthorization != null) {
                    preSignedCopy = new FileTypeAttachment({
                        AttachmentId: printAuthorization.AttachmentId,
                        description: `${form.FormName} Sign.`,
                        date: printAuthorization.CreatedDatetime,
                        radPdfFormId: form.FormId
                    });
                }
            }

            if (mode === ExportMode.Print && isSignedByCurrentUser) {
                preSignedCopy.print();
                return;
            }

            let isSyncToPortalAfterEnabled =
                !form.SharedItem // no shared item yet
                || !form.SharedItem.IncludeLinkedItem // linked item document not yet sent (only messages)
                || (form.SharedItem.Status != PortalSyncStatus.Pending && form.SharedItem.Status != PortalSyncStatus.Viewed)
                || (form.LastUpdateDate && (form.SharedItemDate < form.LastUpdateDate || form.SharedItemFileDate < form.LastUpdateDate)); // form is unsynchronized

            // set up exportableFile as "unsigned file in model"

            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.FaxPrintExportableFile, {
                config: {
                    sentFaxItemId: form.attachmentId ?? form.Id,
                    exportableFile: exportableFile,
                    preSignedCopy: preSignedCopy,
                    exportMode: mode,
                    patientContactId: props.patientId,
                    onApplyOverlaysCallback: () => patientStore.updateContributorsEncountersData(props.patientId, props.currentEncounter.Id),
                    isSyncToPortalAfterEnabled: props.patientModel.IsRegisteredToPortal() && isSyncToPortalAfterEnabled,
                    useSignedCopyOverride: isSignedAuthorizationEnabled && isSignedByCurrentUser,
                    isSaveSignatureEnabled: canUserSign,
                    syncToPortalAfterAction: currentPreview => onPdfFormPortalSyncClick(form, currentPreview, true),
                    onSignButtonCallback: () => protectFormAndReloadData(props.currentEncounter.Id, props.patientId),
                    encounterId: props.noteModel.get("Encounter").Id
                } as IFaxPrintExportableFileConfig
            });
        },
        [getText, props.currentEncounter.Id, props.noteModel, props.patientId, props.patientModel]
    );

    const onPdfFormPortalSyncClick = (form: PdfFormInboxItem, exportableFile: ExportableFile = null, skipSignature: boolean = false): void => {
        let skipTheSignature = skipSignature;
        if (exportableFile && form.SignedPrintAuthorizations?.any()) {
            skipTheSignature = true;
            exportableFile.applyOverlayElement(
                new SignPrintAuthorizationOverlayElement({
                    SignedPrintAuthorization: SignedPrintAuthorization.getLast(form.SignedPrintAuthorizations, form.EncounterId)
                })
            );
        }
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.MedicalNoteForm_PortalClick, {
            portalShareableItem: form,
            exportableFilePreview: exportableFile,
            skipSignature: skipTheSignature
        });
    };

    const onRefreshPortalFormStatusClick = (form: PdfFormInboxItem): void => {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.RefreshPortalFormStatus, {
            encounterId: props.noteModel.get("Encounter").Id,
            form: form
        });
    };

    const getPdfFormUrl = (form: PdfFormInboxItem): string => `${cacheStore.RADPdfFormViewUrl}${form.RadDocumentId}?${form.PdfToken}#toolbar=0`;

    const onContinueRevokeDeleteForm = (): void => {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.MedicalNoteForm_PortalStatusRevoke,
            {
                then: () => sessionStore.dispatch(
                    LocalPatientOperations.MedicalNote.DeletePdfForm,
                    { PatientFormId: radPdfFormStates.formToDeleteId }
                )
            }
        );
    };

    const onCollapseFormClick = async (form: PdfFormInboxItem): Promise<void> => {
        if (!form.PdfToken) {
            const request = {
                PatientFormId: form.Id,
                EmployeeId: cacheStore.GetCurrentEmployee().ContactFacet_id,
                EncounterId: props.noteModel?.get("Encounter")?.Id,
                ConsultationDate: props.noteModel?.get("Encounter")?.ConsultationDate,
                EditMode: false
            };

            const response = await Client.postAsync<any>(
                ServerOperations.Server.Myle_Patient_MedicalNote_GenerateToken,
                request
            );
            form.PdfToken = response.data.Token;
        }
        form.IsVisible = !form.IsVisible;

        setPdfFormsData([...pdfFormsData]);
    };

    useEffect(
        () => {
            if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning || props.isReadOnly) {
                return;
            }

            const openExportableFilePopup = (event: MessageEvent): void => {
                const radPdfFormEditUrl: URL = (() => {
                    try {
                        return new URL(cacheStore.RADPdfFormEditUrl);
                    }
                    catch {
                        return null;
                    }
                })();

                if (!radPdfFormEditUrl) {
                    return;
                }

                if (event.origin === radPdfFormEditUrl.origin) {
                    const { eventTriggerKey, radDocumentId, lastModificationDateUtc } = event.data as IMessageEventData;

                    if (!eventTriggerKey || !radDocumentId) {
                        return;
                    }

                    const radPdf = pdfFormsData.find(p => String(p.RadDocumentId) === String(radDocumentId));

                    if (!radPdf) {
                        return;
                    }

                    switch (eventTriggerKey) {
                        case "sign":
                            faxOrPrintRadPdfFile(radPdf, ExportMode.Sign, lastModificationDateUtc);
                            break;

                        case "fax":
                            faxOrPrintRadPdfFile(radPdf, ExportMode.Fax, lastModificationDateUtc);
                            break;

                        case "print":
                            faxOrPrintRadPdfFile(radPdf, ExportMode.Print, lastModificationDateUtc);
                            break;
                    }
                }
            };

            window.addEventListener("message", openExportableFilePopup);

            return () => {
                window.removeEventListener("message", openExportableFilePopup);
            };
        },
        [faxOrPrintRadPdfFile, pdfFormsData, props.isReadOnly]
    );

    return (
        <div className={enableCssForReduceFrictionSignature ? styles.radPfdFormSection : ""}>
            {radPdfFormStates.showRevokeDeleteMessageBeforeDeleting && (
                <Popup
                    title={getText("WarningLabel")}
                    centeredHorizontally
                    onCloseDelegate={() => setRadPdfFormStates(prevState => ({ ...prevState, showRevokeDeleteMessageBeforeDeleting: false }))}
                    manageOverlay
                >
                    <div className="p-body">
                        <p>{getText("DeleteWillRevokeWarningLabel")}</p>
                        <p>{getText("WishToContinueQuestionLabel")}</p>
                    </div>
                    <div className="p-footer">
                        <div className="pull-right">
                            <button
                                className="btn btn-default push-right-5"
                                onClick={() => setRadPdfFormStates(prevState => ({ ...prevState, showRevokeDeleteMessageBeforeDeleting: false }))}
                            >
                                {getText("CancelLabel")}
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    callbackList.current.push(() => onContinueRevokeDeleteForm());
                                    setRadPdfFormStates(prevState => ({ ...prevState, showRevokeDeleteMessageBeforeDeleting: false }));
                                }}
                            >
                                {getText("ContinueLabel")}
                            </button>
                        </div>
                    </div>
                </Popup>
            )}
            {pdfFormsData?.map((form: PdfFormInboxItem, index: number) => {
                let sentFaxesByFormId: SentFaxInfo[] = [...sentFaxes[form.Id] ?? []];
                let inboxItemsForm = new PdfFormInboxItem({ ...form });

                const attachmentId = form?.SignedPrintAuthorizations?.firstOrDefault()?.AttachmentId;
                if (attachmentId) {
                    sentFaxesByFormId = sentFaxesByFormId.concat(sentFaxes[attachmentId] ?? []);
                    inboxItemsForm = new PdfFormInboxItem({
                        ...form,
                        attachmentId: attachmentId
                    });
                }

                return (
                    <div
                        key={form.Id}
                        className={enableCssForReduceFrictionSignature ? styles.pdfFormViewControlFormatted : styles.pdfFormViewControl}
                        style={{
                            display: props.isReadOnly ? "block" : "grid"
                        }}
                    >
                        {form.contextOpen && (
                            <RadPdfFormSettingPopup
                                form={form}
                                encounterId={props.noteModel.get("Encounter").Id}
                                onDeleteClick={() => onDeletePdfForm(form)}
                            />
                        )}
                        <div className={styles.formContainer}>
                            {props.isEditMode
                                ? (
                                    <div className={styles.editModeContainer}>
                                        {/* Settings wheel button */}
                                        <div
                                            className={styles.settingsWheelButton}
                                            onClick={() => showContextMenuForPdf(form)}
                                        >
                                            <span className="glyphicon glyphicon-cog" />
                                        </div>
                                        <div>
                                            <span className={styles.divider} />
                                        </div>
                                        <div className={styles.formNameEditMode}>
                                            <a
                                                className={`text-bold" ${props.isReadOnly ? " no-hover" : ""}`}
                                                title={form.FormName}
                                                onClick={() => onPdfFormClick(form.Id, true)}
                                            >
                                                {form.FormName}
                                            </a>
                                        </div>
                                    </div>
                                )
                                : (
                                    <div className={styles.formNameContainer}>
                                        <a
                                            title={form.FormName}
                                            className={`${props.isReadOnly ? " no-hover" : ""}`}
                                            onClick={() => onPdfFormClick(form.Id, false)}
                                        >
                                            {form.FormName}
                                        </a>
                                    </div>
                                )
                            }

                            <div className={enableCssForReduceFrictionSignature ? styles.actionLinkContainerFormatted : styles.actionLinkContainer}>
                                {!props.isReadOnly && (
                                    <PlanActionLinks
                                        canSignElement
                                        formId={form.Id}
                                        sentFaxInfo={sentFaxesByFormId}
                                        onActionClick={mode => faxOrPrintRadPdfFile(inboxItemsForm, mode)}
                                        onRevokeSignature={() => protectFormAndRevokeSignature(form.Id)}
                                        signedPrintAuthorizations={form.SignedPrintAuthorizations ?? []}
                                        showPortalStatusButton={(props.patientModel.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) && !props.noteModel.get("IsPatientFormStatusStale")}
                                        portalStatusButton={(
                                            <PortalStatusButton
                                                style={{ marginLeft: "10px" }}
                                                onClick={() => onPdfFormPortalSyncClick(form, RadPdfExportableFileHelper.fromPdfFormInboxItem(form))}
                                                title={form.getPortalStatusTooltip()}
                                                {...PortalHelper.getPortalButtonPropsForShareableItem(form)}
                                            />
                                        )}
                                        encounterId={props.noteModel.get("Encounter").Id}
                                        shouldDisableSignButton={formIdsToDisableButtonToSign.contains(form.FormId)}
                                        disable={isFormDataStale}
                                    />

                                )}
                                {props.patientModel.IsRegisteredToPortal() &&
                            props.noteModel.get("IsPatientFormStatusStale") &&
                            !props.isReadOnly && (
                                <div
                                    className="syncFormButton"
                                    onClick={() => onRefreshPortalFormStatusClick(form)}
                                />
                                )
                                }
                                {props.isReadOnly && (
                                    <div className="pull-right">
                                        <span
                                            className={form.IsVisible ? "glyphicon glyphicon-chevron-up" : "glyphicon glyphicon-chevron-down"}
                                            onClick={() => onCollapseFormClick(form)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {props.isReadOnly && form.PdfToken && (
                            <div className={styles.readOnlyContainer}>
                                <div className={`${styles.inlinePdfContainer} ${(!form.IsVisible || !form.PdfToken) ? styles.collapsed : ""}`}>
                                    {form.IsVisible && form.PdfToken && (
                                        <PdfViewer
                                            pdfSource={getPdfFormUrl(form)}
                                            selectedZoomLevelIndex={pdfZoomLevel[index] ?? defaultZoomLevel}
                                            onZoomLevelChanged={(newZoomLevel: number) =>
                                                setPdfZoomLevel(prev =>
                                                    prev?.map(
                                                        (zoomLevel: number, idx: number) => idx === index ? newZoomLevel : zoomLevel
                                                    ) ?? [...prev]
                                                )
                                            }
                                            isReadOnly
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export { RadPdfFormSection };
